/**
 * 🔧 AdminIndex — landing grid for every owner-only admin tool.
 *
 * One stop to reach Heroes, Bosses, Gear (tradeable flags), Promo Codes,
 * Partners, Supporters, FAQs, Billing. Each tile is owner-gated by the
 * server too — this is just a discoverability surface.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const TOOLS = [
  { path: "/admin/heroes",     icon: "🦸", title: "Hero Stats",       desc: "Tune HP / ATK / DEF / SPD / CRIT per pet tier. Live — no redeploy." },
  { path: "/admin/hero-codex", icon: "📖", title: "Hero Codex Editor", desc: "Live-edit class meta + skill kits (name, Lv gate, cost, tagline). Overrides merge into canon." },
  { path: "/admin/bosses",     icon: "👁️", title: "Boss Stats",       desc: "Edit any boss's multipliers, rewards, window. Active raids keep current pool." },
  { path: "/admin/gear",       icon: "🛡️", title: "Gear Catalog",     desc: "Flip the tradeable flag on any non-soulbound item." },
  { path: "/admin/promo-codes", icon: "🎟️", title: "Promo Codes",     desc: "Create / toggle / delete one-time and reusable promo codes." },
  { path: "/admin/partners",   icon: "🤝", title: "Affiliate Partners", desc: "Manage partner brands surfaced in the Affiliate Wall." },
  { path: "/admin/supporters", icon: "💎", title: "Supporters",       desc: "Curate the Supporter Wall — names, links, tier badges." },
  { path: "/admin/faqs",       icon: "❓", title: "FAQs",             desc: "Edit the public FAQ entries." },
  { path: "/admin/billing",    icon: "💳", title: "Billing",          desc: "Stripe customer / subscription lookup." },
  { path: "/admin/dfcl-decks",  icon: "🃏", title: "DFCL Decks",       desc: "Hand out Flame Card Legends decks by email. Idempotent grant + revoke." },
  { path: "/admin/dfcl-binder", icon: "🔒", title: "DFCL Binder",      desc: "Unlock discovery cards (ShadowFlame, Bulldozer Jab, Frontline Plate) for a player by email." },
  { path: "/admin/event-scheduler", icon: "📅", title: "Event Scheduler", desc: "Flip Phoenix Summit · Jewel Hunt · Fireworks on and off at will. Set a window or kill-switch instantly." },
  { path: "/admin/hero-codex",      icon: "📖", title: "Hero Codex Editor", desc: "Rewrite tagline, long description, lore, base stats and per-skill blurbs for any of the 22 classes. Changes are live for all players on next load." },
  { path: "/admin/hero-swap",   icon: "☯️", title: "Hero Class Swap",  desc: "Free-will class change for any user by email. No Soul Key consumed, no gates." },
];

export default function AdminIndex() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isOwner = user && (user.role === "owner" || (user.email || "").toLowerCase() === "omnimuscle@yahoo.com");

  useEffect(() => {
    document.title = "🔧 Admin · OMNIMUSCLE";
  }, []);

  if (loading) return null;
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="admin-index-locked">
        🔒 Owner only.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="admin-index-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl mb-1">🔧 Admin Console</h1>
        <p className="text-xs text-neutral-500">
          Every owner-only tool, one click away. Tools mutate live data — heads-up.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {TOOLS.map((t) => (
          <button
            key={t.path}
            onClick={() => navigate(t.path)}
            data-testid={`admin-tile-${t.path.replace("/admin/", "")}`}
            className="text-left bg-[#0F0F0F] border-2 border-neutral-800 hover:border-[#D4AF37] hover:bg-[#1a1a1a] transition p-4 group"
          >
            <div className="flex items-start gap-3">
              <span className="text-3xl leading-none">{t.icon}</span>
              <div className="min-w-0 flex-1">
                <div className="font-display text-base group-hover:text-[#FCD34D] transition">{t.title}</div>
                <div className="text-[11px] text-neutral-500 leading-snug mt-0.5">{t.desc}</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-700 font-mono mt-2">{t.path}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 text-[10px] text-neutral-600 leading-relaxed bg-neutral-950 border border-neutral-900 p-3">
        💡 <span className="text-neutral-400">Tip</span>: changes to Heroes / Bosses / Gear are cached
        for 30s server-side and flush automatically on save — next fight (or next listing) uses the new values.
      </div>
    </div>
  );
}
